import { jsx } from '@amazon/vinyl-tsx'
import type { MutableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { createDisposer } from '@amazon/vinyl-util'
import { windowEvents } from '@/util/interaction'
import { toast } from '@/components/toast'
import { getString } from '@/library/i18n/bundle.generated'

export interface NewPlaylistMenuProps {
    readonly visible: MutableValue<boolean>
    readonly onCurate: () => void
}

/**
 * Two-option popover anchored to the New Playlist button: Curate
 * (opens the create-by-name modal) or Create with AI (Maestro,
 * stubbed for now since the LLM playlist surface is its own
 * project). Closes on outside click or after a selection.
 */
export function NewPlaylistMenu(props: NewPlaylistMenuProps) {
    const close = () => (props.visible.value = false)

    return (
        <div
            className="newPlaylistMenu"
            visible={props.visible}
            onConnect={(view) => {
                const { add, dispose } = createDisposer()
                add(
                    windowEvents.on('click', (event) => {
                        if (!props.visible.value) return
                        const target = event.target as HTMLElement
                        // Click on the menu itself is fine.
                        if (view.contains(target)) return
                        // The button itself toggles the menu — let
                        // its own click handler win on the trip up.
                        if (target.closest('.newPlaylistBtn')) return
                        close()
                    })
                )
                return dispose
            }}
        >
            <button
                className="newPlaylistMenuItem"
                onclick={() => {
                    close()
                    props.onCurate()
                }}
            >
                <Icon icon="addToPlaylist" className="newPlaylistMenuItemIcon" />
                <div className="newPlaylistMenuItemText">
                    <div className="newPlaylistMenuItemTitle">
                        {getString('dmusic_library_playlists_curate_playlist_title')}
                    </div>
                    <div className="newPlaylistMenuItemSecondary">
                        {getString('dmusic_library_playlists_curate_playlist_secondary_text')}
                    </div>
                </div>
            </button>
            <button
                className="newPlaylistMenuItem"
                onclick={() => {
                    close()
                    // Maestro-style LLM playlist creation isn't wired
                    // yet — surface the flow in the menu so the
                    // affordance is discoverable and toast a "soon"
                    // message until the backend is in place.
                    toast('Create with AI is coming soon')
                }}
            >
                <Icon icon="maestro" className="newPlaylistMenuItemIcon" />
                <div className="newPlaylistMenuItemText">
                    <div className="newPlaylistMenuItemTitle">
                        {getString('dmusic_library_playlists_curate_playlist_maestro_title')}
                    </div>
                    <div className="newPlaylistMenuItemSecondary">
                        {getString(
                            'dmusic_library_playlists_curate_playlist_maestro_secondary_text'
                        )}
                    </div>
                </div>
            </button>
        </div>
    )
}
